<template>
    <BaseModal
        v-model="model"
        size="md">
        <div class="flex flex-col items-center justify-center m-8">
            <LogoImage class="h-12" />
            <div>
                <small class="text-neutral-400 dark:text-gray-300">{{ runtimeConfig.public.frontendUrl }}</small>
            </div>
        </div>

        <div class="text-center">
            <div class="flex flex-col items-center justify-center m-4">
                <h2 class="font-bold">
                    {{ runtimeConfig.public.clientVersion }}
                </h2>
                <div
                    v-if="versionIsUpToDate"
                    class="text-neutral-400 dark:text-gray-300 flex items-center gap-1">
                    <small>{{ $t('users.info.isCurrentVersion') }}</small>
                    <Icon
                        name="check-circle"
                        color="success" />
                </div>
                <div
                    v-else-if="versionMismatch"
                    class="text-neutral-400 dark:text-gray-300 flex items-center gap-1 cursor-pointer"
                    :title="$t('users.info.loadNewestVersion')"
                    @click="reloadPage">
                    <small>{{ $t('users.info.isOldVersion') }}</small>
                    <Icon
                        name="x-circle"
                        color="danger" />
                </div>
            </div>
        </div>

        <div v-if="useFeature('shortcuts') && isMobileDevice() === false">
            <div class="relative m-6">
                <div
                    class="absolute inset-0 flex items-center"
                    aria-hidden="true">
                    <div class="w-full border-t border-gray-300" />
                </div>
                <div class="relative flex justify-center">
                    <span
                        class="bg-white dark:bg-gray-700 px-3 text-base font-semibold leading-6 text-gray-900 dark:text-white">{{
                        $t('users.info.shortcuts')
                    }}</span>
                </div>
            </div>

            <div
                class="shortcuts max-h-[16vh] overflow-y-auto shadow-inner p-2 rounded border border-neutral-200 dark:border-neutral-300 dark:shadow-neutral-300">
                <ul
                    class="px-2 space-y-1 text-sm"
                    aria-label="Keyboard Shortcuts">
                    <li
                        v-for="s in shortcuts"
                        :key="s.id"
                        class="flex items-center justify-between gap-3 py-1.5 border-b border-neutral-100 dark:border-neutral-600/40 last:border-b-0">
                        <span class="truncate text-neutral-600 dark:text-neutral-300 font-medium">{{ s.label }}</span>
                        <span
                            class="inline-flex items-center rounded bg-neutral-100 dark:bg-neutral-600/60 border border-neutral-300 dark:border-neutral-500 px-2 h-5 font-mono text-[10px] tracking-wide text-neutral-700 dark:text-neutral-100 shadow-sm">
                            {{ s.display }}
                        </span>
                    </li>
                </ul>
            </div>
        </div>

        <div class="relative m-6">
            <div
                class="absolute inset-0 flex items-center"
                aria-hidden="true">
                <div class="w-full border-t border-gray-300" />
            </div>
            <div class="relative flex justify-center">
                <span
                    class="bg-white dark:bg-gray-700 px-3 text-base font-semibold leading-6 text-gray-900 dark:text-white">{{
                    $t('users.info.releaseNotes')
                }}</span>
            </div>
        </div>

        <ModuleScope
            module="app"
            class="max-h-[45vh] overflow-y-auto ">
            <ReleaseNotes />
        </ModuleScope>
    </BaseModal>
</template>

<script setup>

import ReleaseNotes from '@/components/layouts/UserMenu/ReleaseNotes'
import {useAuthStore} from '@/store/auth'

const props = defineProps({
    modelValue: {
        type: Boolean
    }
})

const emits = defineEmits(['update:modelValue'])

const model = computed({
    get: () => props.modelValue,
    set: (newValue) => {
        emits('update:modelValue', newValue)
    }
})

const authStore = useAuthStore()
const runtimeConfig = useRuntimeConfig()
const {versionIsUpToDate, versionMismatch} = useAppVersion()

const shortcuts = computed(() => {
    return [
        {id: 'globalSearch', shortcut: 'meta_k', label: $t('users.info.shortcutList.globalSearch')},
        {id: 'create', shortcut: 'meta_enter', label: $t('users.info.shortcutList.create')},
        {id: 'submit', shortcut: 'meta_enter', label: $t('users.info.shortcutList.submit')},
        {id: 'saveAndBack', shortcut: 'shift_meta_enter', label: $t('users.info.shortcutList.saveAndBack')},
        {id: 'cancel', shortcut: 'meta_backspace', label: $t('users.info.shortcutList.cancel')},
        {id: 'close', shortcut: 'meta_x', label: $t('users.info.shortcutList.close')}
    ].map(s => ({
        ...s,
        display: useKeymap(s.shortcut)
    }))
})

const reloadPage = () => {
    authStore.setRemoteVersion(null)
    window.location.reload()
}
</script>
